// @ts-strict-check
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { LineChart, BarChart } from '../charts';
import { LoadingSpinner, ErrorMessage, KPICard, ChartInfoButton, ChartGuideModal, ChartGuideBody, ColumnInfoTooltip, HorizontalTableShell } from '../ui';
import { formatNumber, formatCurrency } from '../../utils';
import { useTheme } from '../../contexts/ThemeContext';
import { useTimeFrame } from '../../contexts/TimeFrameContext';
import { getChartColors, getChartBackgroundColor, getChartJsOptions } from '../../utils/themeHelpers';
import { zeroBaselinePlugin } from '../../utils/chartPlugins';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';
import { TimePeriod } from '../../types/api';
import { useClubMonthlyPerformance } from '../../hooks/useClubSections';
import {
  getRowGrowthValue,
  getRowMonthKey,
  getYearMonthForRow,
  RowGrowthType,
  sliceClubDisplayWindow,
} from '../../utils/clubGrowthMetrics';
import ChartControls from '../ui/ChartControls';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  Plugin,
} from 'chart.js';

// Register ChartJS components including LogarithmicScale
ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// تبدیل ارقام لاتین به فارسی برای نمایش در نمودارها
const toPersianDigits = (input: string | number): string =>
  String(input).replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]);

interface MonthlyPerformanceTableProps {
  branchId?: number;
  currency?: string;
  timeRange?: TimePeriod;
  onError?: (error: Error) => void;
  showLoading?: boolean;
  compact?: boolean;
  title?: string;
  visibleColumns?: string[];
  dashboardSource?: string;
  guideKey?: keyof typeof CHART_GUIDES;
}

type ComparisonDataPoint = {
  month: string;
  branch: number;
  brand: number;
  allRestaurants: number;
};

interface BranchData {
  month: string;
  monthNum: string;
  year?: number;
  invoiceCount: number;
  subscription: number;
  totalAmount: number;
  avgInvoice: number;
  avgItemPrice: number;
  avgItemCount: number;
  priceGrowth: number | null;
  basketGrowth: number | null;
  creditInvoiceCount: number;
  creditInvoiceAmount: number;
  usedCredit: number;
  uniqueCustomers: number;
  creditPercent: number;
  creditUsedPercent: number;
}

interface KpiThresholdPoint {
  yearMonth: string;
  year: number;
  monthNum: number;
  value: number | null;
}

interface KpiThresholds {
  creditInvoiceCount?: KpiThresholdPoint[];
  creditInvoiceAmount?: KpiThresholdPoint[];
  [key: string]: KpiThresholdPoint[] | undefined;
}

interface ClubDashboardData {
  branchData: BranchData[];
  brandData: BranchData[];
  allRestaurantsData: BranchData[];
  branchAverageData: BranchAverageData[];
  kpiThresholds?: KpiThresholds;
  baseMonths?: number;
  branchInfo: {
    BranchID: number;
    BranchName: string;
    BrandId: number;
  };
}

interface BranchAverageData {
  invoiceCount: number;
  subscription: number;
  totalAmount: number;
  avgInvoice: number;
  avgItemPrice: number;
  avgItemCount: number;
  creditInvoiceCount: number;
  creditInvoiceAmount: number;
  usedCredit: number;
  uniqueCustomers: number;
  creditPercent: number;
  creditUsedPercent: number;
}

// Extended interface for table row data
interface TableRowData extends BranchData {
  invoiceCountPercent: number;
  totalAmountPercent: number;
  subscriptionPercent: number;
  creditPercentPercent: number;
  rowIndex: number;
  uniqueKey: string;
}

interface SparklineData {
  points: number[];
  values: number[];
}

const SPARKLINE_COLUMNS = new Set<string>([
  'invoiceCount',
  'totalAmount'
]);

const COMPARISON_SPARKLINE_COLUMNS = new Set<string>([
  'creditPercent',
  'creditUsedPercent'
]);

// ستون‌هایی که به‌جای مقایسه با بازار، با مقدار هدف (MINVALUE) از BI_KPI مقایسه می‌شوند
const COMPARISON_THRESHOLD_KEY: Record<string, string> = {
  creditPercent: 'creditInvoiceCount',
  creditUsedPercent: 'creditInvoiceAmount'
};

const PERCENTAGE_BAR_COLUMNS = new Set<string>([
  'subscription',
  'invoiceCountGrowth',
  'totalAmountGrowth',
  'priceGrowth',
  'basketGrowth',
  'creditPercent',
  'creditUsedPercent'
]);

const AVERAGE_EXCLUDED_COLUMNS = new Set<string>([
  'invoiceCountGrowth',
  'totalAmountGrowth',
  'priceGrowth',
  'basketGrowth'
]);

const SUM_EXCLUDED_COLUMNS = new Set<string>([
  ...AVERAGE_EXCLUDED_COLUMNS,
  'subscription',
  'avgInvoice',
  'avgItemPrice',
  'avgItemCount'
]);

// Define column type
interface TableColumn {
  key: keyof BranchData | 'month' | 'invoiceCountGrowth' | 'totalAmountGrowth';
  label: string;
  sortable?: boolean;
  sortKey?: string;
  clickable?: boolean;
  tooltip?: string;
  render: (value: string | number | null, row?: BranchData) => React.ReactNode;
}

interface BranchTotalsData {
  invoiceCount: number;
  totalAmount: number;
  creditInvoiceCount: number;
  creditInvoiceAmount: number;
  usedCredit: number;
  uniqueCustomers: number;
  creditPercent: number | null;
  creditUsedPercent: number | null;
}

const MonthlyPerformanceTable: React.FC<MonthlyPerformanceTableProps> = ({
  branchId = 0, // Default to اندرزگو branch
  currency,
  timeRange,
  onError,
  showLoading = true,
  compact = false,
  title = 'جدول عملکرد ماهانه',
  visibleColumns,
  dashboardSource = 'club',
  guideKey = 'monthlyPerformance',
}) => {
  const { currentTheme } = useTheme();
  const { apiTimeFrame } = useTimeFrame();
  const { isModalOpen, currentGuide, openGuide, closeGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const chartRef = useRef<HTMLDivElement>(null);

  // Get theme-aware chart colors
  const chartColors = getChartColors(currentTheme);

  // Use provided timeRange or fall back to context
  const effectiveTimeRange = timeRange || apiTimeFrame;

  // Fetch data
  const { data, isLoading, error, refetch } = useClubMonthlyPerformance(branchId, effectiveTimeRange);

  // Component metadata
  const componentId = `monthly-performance-table-${title}`;
  const componentTitle = title;

  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [sortColumn, setSortColumn] = useState<string>('monthNum');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const sortTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const chartSectionRef = useRef<HTMLDivElement | null>(null);
  const shouldUseBarChart = selectedColumn ? PERCENTAGE_BAR_COLUMNS.has(selectedColumn) : false;

  const displayBranchData = useMemo((): BranchData[] => {
    if (!data?.branchData?.length) return [];
    return sliceClubDisplayWindow(
      data.branchData as BranchData[],
      data.baseMonths ?? data.branchData.length
    );
  }, [data?.branchData, data?.baseMonths]);

  const renderSubscriptionStatusIcon = useCallback((subscriptionValue: number) => {
    if (Math.abs(subscriptionValue - 100) < 0.0001) {
      return (
        <span
          className="w-5 h-5 inline-flex items-center justify-center rounded-md text-xs font-bold text-status-success"
          style={{ backgroundColor: 'rgba(var(--status-success-rgb, 5, 150, 105), 0.14)' }}
          title="هشدار سبز"
        >
          ⚠
        </span>
      );
    }
    if (subscriptionValue > 75) {
      return (
        <span
          className="w-5 h-5 inline-flex items-center justify-center rounded-md text-xs font-bold text-status-success"
          style={{ backgroundColor: 'rgba(var(--status-success-rgb, 5, 150, 105), 0.14)' }}
          title="عملکرد خوب"
        >
          ✓
        </span>
      );
    }
    if (subscriptionValue >= 50) {
      return (
        <span
          className="w-5 h-5 inline-flex items-center justify-center rounded-md text-xs font-bold text-status-warning"
          style={{ backgroundColor: 'rgba(var(--status-warning-rgb, 217, 119, 6), 0.16)' }}
          title="نیاز به توجه"
        >
          ⚠
        </span>
      );
    }
    return (
      <span
        className="w-5 h-5 inline-flex items-center justify-center rounded-md text-xs font-bold text-status-error"
        style={{ backgroundColor: 'rgba(var(--status-error-rgb, 220, 38, 38), 0.14)' }}
        title="عملکرد ضعیف"
      >
        ✕
      </span>
    );
  }, []);

  const getMetricValue = useCallback((
    row: BranchData | undefined,
    metricKey: string,
    sourceData: BranchData[]
  ): number => {
    if (!row) return 0;

    if (
      metricKey === 'invoiceCountGrowth' ||
      metricKey === 'totalAmountGrowth' ||
      metricKey === 'priceGrowth' ||
      metricKey === 'basketGrowth'
    ) {
      return getRowGrowthValue(sourceData, row, metricKey as RowGrowthType) ?? 0;
    }

    if (metricKey === 'creditPercent') {
      if (typeof row.creditPercent === 'number' && Number.isFinite(row.creditPercent)) {
        return row.creditPercent;
      }
      return row.invoiceCount > 0 ? (row.creditInvoiceCount / row.invoiceCount) * 100 : 0;
    }

    if (metricKey === 'creditUsedPercent') {
      if (typeof row.creditUsedPercent === 'number' && Number.isFinite(row.creditUsedPercent)) {
        return row.creditUsedPercent;
      }
      return row.totalAmount > 0 ? (row.usedCredit / row.totalAmount) * 100 : 0;
    }

    const value = row[metricKey as keyof BranchData];
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }, []);

  // Lightning-fast sorting with minimal overhead
  const sortedBranchData = useMemo(() => {
    if (!displayBranchData.length) return [];

    const items = displayBranchData;
    const key = sortColumn;
    const isDesc = sortDirection === 'desc';

    // Ultra-fast month sorting with year consideration
    if (key === 'monthNum' || key === 'month') {
      const result = items.slice().sort((a: BranchData, b: BranchData) => {
        // Extract year from month string or use year field if available
        const getYearMonth = (item: BranchData) => {
          // If year field is present, use it
          if (item.year) {
            return { year: item.year, monthNum: parseInt(item.monthNum) };
          }
          // Otherwise extract from month string (assuming format is "YYYY-MM" or similar)
          const parts = item.month.split('-');
          const year = parts.length > 1 ? parseInt(parts[0]) : new Date().getFullYear();
          const monthNum = parseInt(item.monthNum);
          return { year, monthNum };
        };

        const aData = getYearMonth(a);
        const bData = getYearMonth(b);

        // Sort by year first, then by month
        if (aData.year !== bData.year) {
          return isDesc ? bData.year - aData.year : aData.year - bData.year;
        }
        return isDesc ? bData.monthNum - aData.monthNum : aData.monthNum - bData.monthNum;
      });
      return result;
    }

    // Ultra-fast numeric sorting with direct property access
    const sortKey = key as keyof BranchData;

    if (key === 'invoiceCount') {
      return items.slice().sort((a: BranchData, b: BranchData) => isDesc ? b.invoiceCount - a.invoiceCount : a.invoiceCount - b.invoiceCount);
    }
    if (key === 'totalAmount') {
      return items.slice().sort((a: BranchData, b: BranchData) => isDesc ? b.totalAmount - a.totalAmount : a.totalAmount - b.totalAmount);
    }
    if (key === 'avgInvoice') {
      return items.slice().sort((a: BranchData, b: BranchData) => isDesc ? b.avgInvoice - a.avgInvoice : a.avgInvoice - b.avgInvoice);
    }
    if (key === 'avgItemPrice') {
      return items.slice().sort((a: BranchData, b: BranchData) => isDesc ? b.avgItemPrice - a.avgItemPrice : a.avgItemPrice - b.avgItemPrice);
    }
    if (key === 'avgItemCount') {
      return items.slice().sort((a: BranchData, b: BranchData) => isDesc ? b.avgItemCount - a.avgItemCount : a.avgItemCount - b.avgItemCount);
    }
    if (key === 'subscription') {
      return items.slice().sort((a: BranchData, b: BranchData) => isDesc ? b.subscription - a.subscription : a.subscription - b.subscription);
    }

    if (
      key === 'invoiceCountGrowth' ||
      key === 'totalAmountGrowth' ||
      key === 'priceGrowth' ||
      key === 'basketGrowth'
    ) {
      const growthSource = (data?.branchData || []) as BranchData[];
      return items.slice().sort((a: BranchData, b: BranchData) => {
        const aVal = getRowGrowthValue(growthSource, a, key as RowGrowthType);
        const bVal = getRowGrowthValue(growthSource, b, key as RowGrowthType);
        if (aVal === null && bVal === null) return 0;
        if (aVal === null) return 1;
        if (bVal === null) return -1;
        return isDesc ? bVal - aVal : aVal - bVal;
      });
    }

    // Generic fallback
    return items.slice().sort((a: BranchData, b: BranchData) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return isDesc ? bVal - aVal : aVal - bVal;
      }
      const result = String(aVal || '').localeCompare(String(bVal || ''));
      return isDesc ? -result : result;
    });
  }, [data?.branchData, displayBranchData, sortColumn, sortDirection]);

  const branchTotalsRow = useMemo((): BranchTotalsData | null => {
    if (!sortedBranchData.length) return null;

    let invoiceCount = 0;
    let totalAmount = 0;
    let creditInvoiceCount = 0;
    let creditInvoiceAmount = 0;
    let usedCredit = 0;
    let uniqueCustomers = 0;

    for (const row of sortedBranchData) {
      invoiceCount += row.invoiceCount;
      totalAmount += row.totalAmount;
      creditInvoiceCount += row.creditInvoiceCount;
      creditInvoiceAmount += row.creditInvoiceAmount;
      usedCredit += row.usedCredit;
      uniqueCustomers += row.uniqueCustomers;
    }

    return {
      invoiceCount,
      totalAmount,
      creditInvoiceCount,
      creditInvoiceAmount,
      usedCredit,
      uniqueCustomers,
      creditPercent: totalAmount > 0
        ? Number(((creditInvoiceAmount / totalAmount) * 100).toFixed(2))
        : null,
      creditUsedPercent: totalAmount > 0
        ? Number(((usedCredit / totalAmount) * 100).toFixed(2))
        : null
    };
  }, [sortedBranchData]);

  // Pre-calculate max values for progress bars (huge performance boost)
  const maxValues = useMemo(() => {
    if (!sortedBranchData.length) return { maxInvoiceCount: 1, maxTotalAmount: 1 };

    let maxInvoiceCount = 0;
    let maxTotalAmount = 0;

    for (const row of sortedBranchData) {
      if (row.invoiceCount > maxInvoiceCount) maxInvoiceCount = row.invoiceCount;
      if (row.totalAmount > maxTotalAmount) maxTotalAmount = row.totalAmount;
    }

    return {
      maxInvoiceCount: maxInvoiceCount || 1,
      maxTotalAmount: maxTotalAmount || 1
    };
  }, [sortedBranchData]);

  // Pre-calculate row data with progress percentages for faster rendering
  const tableRowsData = useMemo((): TableRowData[] => {
    if (!sortedBranchData.length) return [];

    const maxInvoice = maxValues.maxInvoiceCount;
    const maxTotal = maxValues.maxTotalAmount;

    return sortedBranchData.map((row: BranchData, index: number) => ({
      ...row,
      invoiceCountPercent: (row.invoiceCount / maxInvoice) * 100,
      totalAmountPercent: (row.totalAmount / maxTotal) * 100,
      subscriptionPercent: Math.min(row.subscription, 100),
      creditPercentPercent: Math.min(row.creditPercent, 100),
      rowIndex: index,
      // Pre-calculate unique key to prevent re-renders
      uniqueKey: `${row.monthNum}-${index}`
    }));
  }, [sortedBranchData, maxValues.maxInvoiceCount, maxValues.maxTotalAmount]);

  const brandDataByMonth = useMemo(() => {
    const map = new Map<string, BranchData>();
    (data?.brandData || []).forEach((row: BranchData) => {
      map.set(getRowMonthKey(row), row);
    });
    return map;
  }, [data?.brandData, getRowMonthKey]);

  const allRestaurantsDataByMonth = useMemo(() => {
    const map = new Map<string, BranchData>();
    (data?.allRestaurantsData || []).forEach((row: BranchData) => {
      map.set(getRowMonthKey(row), row);
    });
    return map;
  }, [data?.allRestaurantsData, getRowMonthKey]);

  // نگاشت مقادیر هدف (MINVALUE) برای هر KPI بر اساس کلید ماه
  const thresholdMapsByKpi = useMemo(() => {
    const maps: Record<string, Map<string, number>> = {};
    const thresholds = (data as ClubDashboardData | undefined)?.kpiThresholds;
    if (thresholds) {
      Object.entries(thresholds).forEach(([kpiName, points]) => {
        if (!points) return;
        const map = new Map<string, number>();
        points.forEach((point) => {
          if (point.value === null || !Number.isFinite(point.value)) return;
          const key = `${point.year}-${String(point.monthNum).padStart(2, '0')}`;
          map.set(key, point.value);
        });
        maps[kpiName] = map;
      });
    }
    return maps;
  }, [data]);

  // مقدار هدف ماهانه برای ستون موردنظر (در صورت وجود)
  const getThresholdValue = useCallback((columnKey: string, row: BranchData): number | null => {
    const thresholdName = COMPARISON_THRESHOLD_KEY[columnKey];
    if (!thresholdName) return null;
    const map = thresholdMapsByKpi[thresholdName];
    if (!map) return null;
    const value = map.get(getRowMonthKey(row));
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }, [thresholdMapsByKpi, getRowMonthKey]);

  const branchAverageRow = useMemo<BranchAverageData | null>(() => {
    const averageSource = (data as ClubDashboardData | undefined)?.branchAverageData?.[0];
    if (!averageSource) return null;

    return {
      invoiceCount: Number(averageSource.invoiceCount) || 0,
      subscription: Number(averageSource.subscription) || 0,
      totalAmount: Number(averageSource.totalAmount) || 0,
      avgInvoice: Number(averageSource.avgInvoice) || 0,
      avgItemPrice: Number(averageSource.avgItemPrice) || 0,
      avgItemCount: Number(averageSource.avgItemCount) || 0,
      creditInvoiceCount: Number(averageSource.creditInvoiceCount) || 0,
      creditInvoiceAmount: Number(averageSource.creditInvoiceAmount) || 0,
      usedCredit: Number(averageSource.usedCredit) || 0,
      uniqueCustomers: Number(averageSource.uniqueCustomers) || 0,
      creditPercent: Number(averageSource.creditPercent) || 0,
      creditUsedPercent: Number(averageSource.creditUsedPercent) || 0
    };
  }, [data]);

  const handleColumnClick = useCallback((column: string) => {
    setSelectedColumn(column);
    setShowChart(true);

    // Smooth scroll to chart section after a short delay to ensure it's rendered
    setTimeout(() => {
      if (chartSectionRef.current) {
        chartSectionRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  }, []);

  const handleSort = useCallback((column: string) => {
    // Clear any pending sort operations
    if (sortTimeoutRef.current) {
      clearTimeout(sortTimeoutRef.current);
    }

    // Immediate state update for UI responsiveness
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      if (sortDirection !== 'asc') {
        setSortDirection('asc');
      }
    }
  }, [sortColumn, sortDirection]);

  const formatNumberDisplay = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '-';
    return formatNumber(num);
  };

  const formatCurrencyDisplay = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '-';
    return formatCurrency(Number(num).toFixed(2), currency);
  };

  const formatPercent = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '-';
    return `${num.toFixed(2)}%`;
  };

  const percentile = useCallback((values: number[], p: number): number => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const index = (sorted.length - 1) * p;
    const low = Math.floor(index);
    const high = Math.ceil(index);
    if (low === high) return sorted[low];
    return sorted[low] + (sorted[high] - sorted[low]) * (index - low);
  }, []);

  const getSparklineData = useCallback((key: string): SparklineData | null => {
    if (!sortedBranchData.length) return null;

    const growthSource = (data?.branchData || []) as BranchData[];
    const rawValues: number[] = sortedBranchData.map((row) => {
      if (
        key === 'invoiceCountGrowth' ||
        key === 'totalAmountGrowth' ||
        key === 'priceGrowth' ||
        key === 'basketGrowth'
      ) {
        return getRowGrowthValue(growthSource, row, key as RowGrowthType) ?? 0;
      }
      const value = row[key as keyof BranchData];
      return typeof value === 'number' ? value : 0;
    });

    if (!rawValues.length) return null;
    const isPercentLike = ['subscription', 'creditPercent', 'creditUsedPercent', 'priceGrowth', 'basketGrowth', 'invoiceCountGrowth', 'totalAmountGrowth'].includes(key);

    if (isPercentLike) {
      const maxAbs = Math.max(1, ...rawValues.map(v => Math.abs(v)));
      return {
        values: rawValues,
        points: rawValues.map(v => ((v + maxAbs) / (2 * maxAbs)) * 100)
      };
    }

    const low = percentile(rawValues, 0.1);
    const high = percentile(rawValues, 0.9);
    const safeRange = Math.max(1, high - low);
    return {
      values: rawValues,
      points: rawValues.map(v => (Math.max(low, Math.min(high, v)) - low) / safeRange * 100)
    };
  }, [data?.branchData, percentile, sortedBranchData]);

  const renderSparkline = useCallback((columnKey: string, rowIndex: number) => {
    if (!SPARKLINE_COLUMNS.has(columnKey)) return null;
    const sparkline = getSparklineData(columnKey);
    if (!sparkline || sparkline.points.length < 2) return null;

    const currentValue = sparkline.values[rowIndex] ?? 0;
    const maxValue = Math.max(1, ...sparkline.values.map(v => Math.abs(v)));
    const percent = Math.max(0, Math.min(100, (Math.abs(currentValue) / maxValue) * 100));
    const barColor = columnKey === 'invoiceCount' ? 'var(--accent-cyan)' : 'var(--status-success)';

    return (
      <div className="mt-1 w-14 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${percent}%`, backgroundColor: barColor }}
        />
      </div>
    );
  }, [getSparklineData]);

  const renderComparisonSparkline = useCallback((
    columnKey: string,
    row: BranchData
  ) => {
    if (!COMPARISON_SPARKLINE_COLUMNS.has(columnKey)) return null;

    const branchValueRaw = getMetricValue(row, columnKey, data?.branchData || []);
    // به‌جای مقایسه با بازار، با مقدار هدف ماهانه (MINVALUE) از BI_KPI مقایسه می‌کنیم
    const thresholdValueRaw = getThresholdValue(columnKey, row);
    if (thresholdValueRaw === null) return null;

    const relativeToBenchmark = thresholdValueRaw > 0
      ? (branchValueRaw / thresholdValueRaw) * 100
      : (branchValueRaw > 0 ? 100 : 0);
    const relativeClamped = Math.max(0, Math.min(200, relativeToBenchmark));
    const fillWidth = relativeClamped / 2; // 50% means برابر مقدار هدف
    const isAboveBenchmark = relativeToBenchmark >= 100;
    const branchColor = isAboveBenchmark ? 'var(--status-success)' : 'var(--status-error)';
    const diff = branchValueRaw - thresholdValueRaw;
    const diffSign = diff >= 0 ? '+' : '';
    const statusLabel = isAboveBenchmark ? 'بالاتر از هدف' : 'پایین‌تر از هدف';

    return (
      <div className="relative group/spark mt-1 w-20 cursor-help">
        <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${fillWidth}%`, backgroundColor: branchColor }}
          />
          <span
            className="absolute top-0 h-full w-0.5"
            style={{
              left: 'calc(50% - 1px)',
              backgroundColor: 'var(--status-warning)'
            }}
          />
        </div>
        <div className="pointer-events-none absolute z-20 bottom-full right-1/2 mb-2 w-44 translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-5 text-white opacity-0 shadow-lg transition-opacity group-hover/spark:opacity-100">
          <div className={`font-bold mb-1 ${isAboveBenchmark ? 'text-green-300' : 'text-red-300'}`}>{statusLabel}</div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">این شعبه</span>
            <span dir="ltr" className="tabular-nums font-semibold">{formatPercent(branchValueRaw)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">مقدار هدف</span>
            <span dir="ltr" className="tabular-nums font-semibold">{formatPercent(thresholdValueRaw)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">اختلاف</span>
            <span dir="ltr" className="tabular-nums font-semibold">{diffSign}{formatPercent(diff)}</span>
          </div>
        </div>
      </div>
    );
  }, [data?.branchData, getMetricValue, getThresholdValue]);

  // مقایسه‌ی مقدار شعبه با یک مقدار مرجع برای همان ماه.
  // برای ستون‌های اعتباری، مرجع = مقدار هدف (MINVALUE) از BI_KPI؛ در غیر این صورت میانگین کل بازار.
  const getMarketComparison = useCallback((
    columnKey: string,
    row: BranchData
  ): { branch: number; market: number; diff: number; better: boolean; benchmarkLabel: string; betterLabel: string; worseLabel: string } | null => {
    const branch = getMetricValue(row, columnKey, data?.branchData || []);
    if (!Number.isFinite(branch)) return null;

    // مقایسه با مقدار هدف برای ستون‌های اعتباری
    if (COMPARISON_THRESHOLD_KEY[columnKey]) {
      const target = getThresholdValue(columnKey, row);
      if (target === null) return null;
      return {
        branch,
        market: target,
        diff: branch - target,
        better: branch >= target,
        benchmarkLabel: 'مقدار هدف',
        betterLabel: 'بالاتر از هدف',
        worseLabel: 'پایین‌تر از هدف'
      };
    }

    // در غیر این صورت مقایسه با میانگین کل بازار
    if (!data?.allRestaurantsData?.length) return null;
    const marketRow = allRestaurantsDataByMonth.get(getRowMonthKey(row));
    if (!marketRow) return null;
    const market = getMetricValue(marketRow, columnKey, data.allRestaurantsData);
    if (!Number.isFinite(market)) return null;
    return {
      branch,
      market,
      diff: branch - market,
      better: branch >= market,
      benchmarkLabel: 'میانگین بازار',
      betterLabel: 'بهتر از میانگین بازار',
      worseLabel: 'ضعیف‌تر از میانگین بازار'
    };
  }, [allRestaurantsDataByMonth, data, getMetricValue, getRowMonthKey, getThresholdValue]);

  // نشانگر کوچک سبز/قرمز نسبت به مرجع + tooltip هم‌سبک با داشبورد customer-grouping
  const renderMarketIndicator = useCallback((columnKey: string, row: BranchData) => {
    const cmp = getMarketComparison(columnKey, row);
    if (!cmp) return null;
    const diffSign = cmp.diff >= 0 ? '+' : '';
    const statusLabel = cmp.better ? cmp.betterLabel : cmp.worseLabel;
    return (
      <div className="relative group/market inline-flex">
        <span
          className="inline-flex h-2.5 w-2.5 rounded-full shrink-0 cursor-help ring-2 ring-white dark:ring-gray-900 transition-transform group-hover/market:scale-125"
          style={{ backgroundColor: cmp.better ? 'var(--status-success)' : 'var(--status-error)' }}
          role="img"
          aria-label={statusLabel}
        />
        <div className="pointer-events-none absolute z-20 bottom-full right-1/2 mb-2 w-44 translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs leading-5 text-white opacity-0 shadow-lg transition-opacity group-hover/market:opacity-100">
          <div className={`font-bold mb-1 ${cmp.better ? 'text-green-300' : 'text-red-300'}`}>{statusLabel}</div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">این شعبه</span>
            <span dir="ltr" className="tabular-nums font-semibold">{formatPercent(cmp.branch)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">{cmp.benchmarkLabel}</span>
            <span dir="ltr" className="tabular-nums font-semibold">{formatPercent(cmp.market)}</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="opacity-80">اختلاف</span>
            <span dir="ltr" className="tabular-nums font-semibold">{diffSign}{formatPercent(cmp.diff)}</span>
          </div>
        </div>
      </div>
    );
  }, [getMarketComparison]);

  const getChartData = useCallback(() => {
    if (!selectedColumn || !data || !displayBranchData.length) return { labels: [], datasets: [] };

    const chartDataPoints = displayBranchData.map((row: BranchData) => {
      const rowKey = getRowMonthKey(row);
      const brandRow = brandDataByMonth.get(rowKey);
      const allRestaurantsRow = allRestaurantsDataByMonth.get(rowKey);

      const branchValue = getMetricValue(row, selectedColumn, data.branchData);
      const brandValue = getMetricValue(brandRow, selectedColumn, data.brandData);
      const allRestaurantsValue = getMetricValue(allRestaurantsRow, selectedColumn, data.allRestaurantsData);

      return {
        month: row.month,
        branch: branchValue,
        brand: brandValue,
        allRestaurants: allRestaurantsValue,
      };
    });

    return {
      labels: chartDataPoints.map(point => point.month),
      datasets: [
        {
          label: `شعبه ${data.branchInfo.BranchName}`,
          data: chartDataPoints.map(point => point.branch),
          borderColor: chartColors[0],
          backgroundColor: getChartBackgroundColor(0, currentTheme, shouldUseBarChart ? 0.8 : 0.25),
          borderWidth: shouldUseBarChart ? 2 : 3,
          borderRadius: shouldUseBarChart ? 6 : undefined,
          pointRadius: shouldUseBarChart ? undefined : 6,
          pointHoverRadius: shouldUseBarChart ? undefined : 8,
          tension: shouldUseBarChart ? undefined : 0.3,
        },
        {
          label: 'کل گروه',
          data: chartDataPoints.map(point => point.brand),
          borderColor: chartColors[2],
          backgroundColor: getChartBackgroundColor(2, currentTheme, shouldUseBarChart ? 0.8 : 0.25),
          borderWidth: 2,
          borderRadius: shouldUseBarChart ? 6 : undefined,
          borderDash: shouldUseBarChart ? undefined : [5, 5],
          pointHoverRadius: shouldUseBarChart ? undefined : 8,
          tension: shouldUseBarChart ? undefined : 0.3,
        },
        {
          label: 'بازار',
          data: chartDataPoints.map(point => point.allRestaurants),
          borderColor: chartColors[1],
          backgroundColor: getChartBackgroundColor(1, currentTheme, shouldUseBarChart ? 0.8 : 0.25),
          borderWidth: 2,
          borderRadius: shouldUseBarChart ? 6 : undefined,
          borderDash: shouldUseBarChart ? undefined : [3, 3],
          pointHoverRadius: shouldUseBarChart ? undefined : 8,
          tension: shouldUseBarChart ? undefined : 0.3,
        }
      ]
    };
  }, [
    allRestaurantsDataByMonth,
    brandDataByMonth,
    chartColors,
    currentTheme,
    data,
    displayBranchData,
    getMetricValue,
    getRowMonthKey,
    selectedColumn,
    shouldUseBarChart
  ]);

  const comparisonChartData = useMemo(() => getChartData(), [getChartData]);
  const comparisonChartPoints = useMemo<ComparisonDataPoint[]>(() => {
    if (!displayBranchData.length || !selectedColumn) return [];

    return displayBranchData.map((row: BranchData) => {
      const rowKey = getRowMonthKey(row);
      const brandRow = brandDataByMonth.get(rowKey);
      const allRestaurantsRow = allRestaurantsDataByMonth.get(rowKey);

      return {
        month: row.month,
        branch: getMetricValue(row, selectedColumn, data.branchData),
        brand: getMetricValue(brandRow, selectedColumn, data.brandData),
        allRestaurants: getMetricValue(allRestaurantsRow, selectedColumn, data.allRestaurantsData),
      };
    });
  }, [allRestaurantsDataByMonth, brandDataByMonth, data, displayBranchData, getMetricValue, getRowMonthKey, selectedColumn]);

  const barChartYAxisBounds = useMemo(() => {
    if (!shouldUseBarChart) return null;

    const values = comparisonChartPoints
      .flatMap(point => [point.branch, point.brand, point.allRestaurants])
      .filter((value: number) => Number.isFinite(value));

    if (values.length === 0) {
      return { min: -1, max: 1 };
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);

    if (minValue === maxValue) {
      const padding = Math.max(1, Math.abs(minValue) * 0.1);
      return {
        min: Math.min(0, minValue - padding),
        max: Math.max(0, maxValue + padding)
      };
    }

    const range = maxValue - minValue;
    const padding = Math.max(range * 0.08, 1);

    if (minValue >= 0) {
      return {
        min: 0,
        max: maxValue + padding
      };
    }

    if (maxValue <= 0) {
      return {
        min: minValue - padding,
        max: 0
      };
    }

    return {
      min: Math.min(0, minValue - padding),
      max: Math.max(0, maxValue + padding)
    };
  }, [comparisonChartPoints, shouldUseBarChart]);

  const getColumnTitle = (column: string) => {
    const titles: Record<string, string> = {
      invoiceCount: 'تعداد سفارشات',
      totalAmount: 'مبلغ سفارش',
      avgInvoice: 'مبلغ هر سفارش',
      avgItemPrice: 'قیمت هر واحد کالا',
      avgItemCount: 'تعداد اقلام هر سفارش',
      uniqueCustomers: 'مشتریان یکتا',
      usedCredit: 'اعتبار مصرفی',
      creditInvoiceCount: 'سفارش‌های اعتباری',
      creditPercent: 'درصد سفارش‌های با اعتبار',
      creditUsedPercent: 'درصد اعتبار استفاده شده',
      invoiceCountGrowth: 'رشد تعداد سفارش',
      totalAmountGrowth: 'رشد میزان فروش'
    };
    return titles[column] || column;
  };

  const formatChartValue = (value: number, column: string) => {
    if (['totalAmount', 'avgInvoice', 'avgItemPrice', 'usedCredit', 'creditInvoiceAmount'].includes(column)) {
      return formatCurrencyDisplay(+value.toFixed(2));
    }
    if (['subscription', 'creditPercent', 'creditUsedPercent', 'invoiceCountGrowth', 'totalAmountGrowth', 'priceGrowth', 'basketGrowth'].includes(column)) {
      return formatPercent(value);
    }
    if (column === 'avgItemCount') {
      return value.toFixed(2);
    }
    return formatNumberDisplay(value);
  };

  const formatTooltipLabelValue = (label: string, value: string): string => {
    const separator = '\u00A0:\u00A0';
    const isolatedValue = `\u2068${value}\u2069`;
    return `${label}${separator}${isolatedValue}`;
  };

  const barValueLabelPlugin = useMemo<Plugin<'bar'>>(() => ({
    id: 'bar-value-labels',
    afterDatasetsDraw: (chart) => {
      const { ctx, chartArea } = chart;
      if (!chartArea) return;

      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = '700 10px Vazirmatn, IRANSans, sans-serif';

      const textHeight = 10;
      const insidePadding = 6; // فاصله‌ی برچسب از انتهای میله وقتی داخل میله است
      const outsideGap = 4;    // فاصله‌ی برچسب از انتهای میله وقتی بیرون میله است
      const labelColor = '#111827'; // مشکی برای همه‌ی برچسب‌ها

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (!meta || meta.hidden) return;

        meta.data.forEach((element: any, dataIndex: number) => {
          const datasetData = dataset.data?.[dataIndex];
          const value = typeof datasetData === 'number' ? datasetData : Number(datasetData);
          if (!Number.isFinite(value)) return;

          // هندسه‌ی میله: انتها (y) و مبنا (base)
          const { x, y, base } = element.getProps(['x', 'y', 'base'], true);
          if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(base)) return;

          const rawLabel = formatChartValue(value, selectedColumn || '');
          if (!rawLabel || rawLabel === '-') return;
          // نمایش درصدها با ارقام فارسی
          const labelText = toPersianDigits(rawLabel);

          const isPositive = value >= 0;
          const barHeight = Math.abs(base - y);
          // اگر ارتفاع میله برای جا دادن برچسب کافی باشد، داخل میله رسم می‌شود
          const fitsInside = barHeight >= textHeight + insidePadding * 2;

          let cy: number;
          if (fitsInside) {
            // داخل میله، نزدیک انتهای آن
            cy = isPositive
              ? y + insidePadding + textHeight / 2
              : y - insidePadding - textHeight / 2;
          } else {
            // میله کوتاه است؛ برچسب بیرون از میله رسم می‌شود
            cy = isPositive
              ? y - outsideGap - textHeight / 2
              : y + outsideGap + textHeight / 2;
          }
          // متن مشکی برای همه‌ی برچسب‌ها + هاله‌ی روشن ملایم برای خوانایی روی رنگ میله
          ctx.fillStyle = labelColor;
          ctx.shadowColor = 'rgba(255, 255, 255, 0.6)';
          ctx.shadowBlur = 3;

          // محدود کردن موقعیت عمودی داخل ناحیه‌ی نمودار
          cy = Math.max(
            chartArea.top + textHeight / 2 + 2,
            Math.min(chartArea.bottom - textHeight / 2 - 2, cy)
          );

          ctx.textBaseline = 'middle';
          ctx.fillText(labelText, x, cy);
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
        });
      });

      ctx.restore();
    }
  }), [currentTheme, formatChartValue, selectedColumn]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری جدول عملکرد ماهانه…"
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <ErrorMessage
          message={`خطا در بارگذاری جدول عملکرد ماهانه: ${(error as Error).message}`}
        />
      </div>
    );
  }

  if (!data || !data.branchData || data.branchData.length === 0) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <h3 className="title-card">جدول عملکرد ماهانه</h3>
        <div className="text-center py-12">
          <p className="text-text-secondary">داده‌ای برای نمایش موجود نیست</p>
        </div>
      </div>
    );
  }

  const tableColumns: TableColumn[] = [
    {
      key: 'month',
      label: 'ماه',
      sortable: true,
      sortKey: 'monthNum',
      render: (value: string) => value
    },
    {
      key: 'invoiceCount',
      label: 'تعداد سفارشات',
      clickable: true,
      sortable: true,
      render: (value: number) => formatNumberDisplay(value)
    },
    {
      key: 'invoiceCountGrowth',
      label: 'رشد تعداد سفارش',
      sortable: true,
      clickable: true,
      render: (value: number | null, row?: BranchData) => {
        if (!row) return '-';
        const growth = data ? getRowGrowthValue(data.branchData, row, 'invoiceCountGrowth') : null;
        if (growth === null) return '-';

        return (
          <div className="flex items-center justify-center gap-1.5">
            {renderMarketIndicator('invoiceCountGrowth', row)}
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold leading-none"
              style={{
                backgroundColor: growth >= 0
                  ? 'rgba(var(--status-success-rgb, 5, 150, 105), 0.1)'
                  : 'rgba(var(--status-error-rgb, 220, 38, 38), 0.1)',
                color: growth >= 0
                  ? 'var(--status-success)'
                  : 'var(--status-error)'
              }}>
              <span className="text-xs">
                {growth >= 0 ? '↗' : '↘'}
              </span>
              <span>{formatPercent(Math.abs(growth))}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'subscription',
      label: 'اشتراک‌گیری',
      clickable: true,
      sortable: true,
      render: (value: number) => formatPercent(value)
    },
    {
      key: 'totalAmount',
      label: 'میزان فروش',
      clickable: true,
      sortable: true,
      render: (value: number) => formatCurrencyDisplay(value)
    },
    {
      key: 'totalAmountGrowth',
      label: 'رشد میزان فروش',
      sortable: true,
      clickable: true,
      render: (value: number | null, row?: BranchData) => {
        if (!row) return '-';
        const growth = data ? getRowGrowthValue(data.branchData, row, 'totalAmountGrowth') : null;
        if (growth === null) return '-';

        return (
          <div className="flex items-center justify-center gap-1.5">
            {renderMarketIndicator('totalAmountGrowth', row)}
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold leading-none"
              style={{
                backgroundColor: growth >= 0
                  ? 'rgba(var(--status-success-rgb, 5, 150, 105), 0.1)'
                  : 'rgba(var(--status-error-rgb, 220, 38, 38), 0.1)',
                color: growth >= 0
                  ? 'var(--status-success)'
                  : 'var(--status-error)'
              }}>
              <span className="text-xs">
                {growth >= 0 ? '↗' : '↘'}
              </span>
              <span>{formatPercent(Math.abs(growth))}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'avgInvoice',
      label: 'مبلغ هر سفارش',
      clickable: true,
      sortable: true,
      render: (value: number) => formatCurrencyDisplay(value)
    },
    {
      key: 'avgItemPrice',
      label: 'قیمت هر واحد کالا',
      clickable: true,
      sortable: true,
      tooltip: 'نشان‌دهنده تورم منو یا تمایل مشتری به غذاهای گران/ارزان.',
      render: (value: number) => formatCurrencyDisplay(value)
    },
    {
      key: 'avgItemCount',
      label: 'تعداد اقلام هر سفارش',
      clickable: true,
      sortable: true,
      tooltip: 'هر مشتری به طور متوسط چند قلم کالا در یک سفارش خریده است.',
      render: (value: number) => value.toFixed(2)
    },
    {
      key: 'priceGrowth',
      label: 'رشد قیمت',
      clickable: true,
      sortable: true,
      render: (_value: number | null, row?: BranchData) => {
        const growth = data && row ? getRowGrowthValue(data.branchData, row, 'priceGrowth') : null;
        if (growth === null) return '-';
        return (
          <div className="flex items-center justify-center gap-1.5">
            {row && renderMarketIndicator('priceGrowth', row)}
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold leading-none"
              style={{
                backgroundColor: growth >= 0
                  ? 'rgba(var(--status-success-rgb, 5, 150, 105), 0.1)'
                  : 'rgba(var(--status-error-rgb, 220, 38, 38), 0.1)',
                color: growth >= 0
                  ? 'var(--status-success)'
                  : 'var(--status-error)'
              }}>
              <span className="text-xs">
                {growth >= 0 ? '↗' : '↘'}
              </span>
              <span>{formatPercent(Math.abs(growth))}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'basketGrowth',
      label: 'رشد سبد',
      clickable: true,
      sortable: true,
      render: (_value: number | null, row?: BranchData) => {
        const growth = data && row ? getRowGrowthValue(data.branchData, row, 'basketGrowth') : null;
        if (growth === null) return '-';
        return (
          <div className="flex items-center justify-center gap-1.5">
            {row && renderMarketIndicator('basketGrowth', row)}
            <div className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold leading-none"
              style={{
                backgroundColor: growth >= 0
                  ? 'rgba(var(--status-success-rgb, 5, 150, 105), 0.1)'
                  : 'rgba(var(--status-error-rgb, 220, 38, 38), 0.1)',
                color: growth >= 0
                  ? 'var(--status-success)'
                  : 'var(--status-error)'
              }}>
              <span className="text-xs">
                {growth >= 0 ? '↗' : '↘'}
              </span>
              <span>{formatPercent(Math.abs(growth))}</span>
            </div>
          </div>
        );
      }
    },
    {
      key: 'creditInvoiceCount',
      label: 'تعداد سفارش‌های با اعتبار',
      clickable: true,
      sortable: true,
      render: (value: number) => formatNumberDisplay(value)
    },
    {
      key: 'creditInvoiceAmount',
      label: 'مبلغ سفارش‌های با اعتبار',
      clickable: true,
      sortable: true,
      render: (value: number) => formatCurrencyDisplay(value)
    },
    {
      key: 'usedCredit',
      label: 'مبلغ اعتبار استفاده شده',
      clickable: true,
      sortable: true,
      render: (value: number) => formatCurrencyDisplay(value)
    },
    {
      key: 'uniqueCustomers',
      label: 'تعداد مشتریان یکتا',
      clickable: false,
      sortable: true,
      render: (value: number) => formatNumberDisplay(value)
    },
    {
      key: 'creditPercent',
      label: 'درصد سفارش‌های با اعتبار',
      clickable: false,
      sortable: true,
      render: (value: number) => formatPercent(value)
    },
    {
      key: 'creditUsedPercent',
      label: 'درصد اعتبار استفاده شده',
      clickable: false,
      sortable: true,
      render: (value: number) => formatPercent(value)
    }
  ];

  // Filter columns for compact mode
  const displayColumns = (() => {
    if (visibleColumns && visibleColumns.length > 0) {
      const columnMap = new Map(tableColumns.map(col => [col.key, col]));
      return visibleColumns
        .map(key => columnMap.get(key as TableColumn['key']))
        .filter((col): col is TableColumn => Boolean(col));
    }
    return compact
      ? tableColumns.filter(col => ['month', 'invoiceCount', 'invoiceCountGrowth', 'totalAmount', 'totalAmountGrowth', 'avgInvoice', 'uniqueCustomers'].includes(col.key))
      : tableColumns;
  })();

  const getColumnLabel = (columnKey: string) => {
    const selectedTableColumn = displayColumns.find((column) => column.key === columnKey);
    return selectedTableColumn?.label || getColumnTitle(columnKey);
  };

  return (
    <>
      <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="table"
          dashboardSource={dashboardSource}
          onInfo={() => openGuide(CHART_GUIDES[guideKey])}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource,
            componentType: 'table',
            description: 'نمایش داده‌های عملکرد ماهانه شعبه با امکان مقایسه با کل گروه',
            props: { branchId, timeRange: effectiveTimeRange, compact },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h3 className="title-card">{componentTitle}</h3>
        <p className="text-sm text-text-secondary mt-1 text-center">
          نمایش داده‌های {effectiveTimeRange === '1month' ? '1' :
            effectiveTimeRange === '3months' ? '3' :
            effectiveTimeRange === '6months' ? '6' : '12'} ماه اخیر شعبه {data.branchInfo.BranchName}
        </p>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
          </div>
        </div>

        {/* Table Container with Enhanced Responsive Design */}
        <HorizontalTableShell minWidth={compact ? 1000 : 1400}>
              <table className="w-full border-collapse" style={{ direction: 'rtl' }}>
                <thead>
                  <tr className="border-b-2 !bg-gray-100 dark:!bg-gray-800" style={{
                    borderColor: 'var(--border-secondary)'
                  }}>
                    {displayColumns.map((col: TableColumn) => (
                      <th
                        key={col.key}
                        className="px-3 py-4 text-center text-xs font-bold border-l last:border-l-0 relative"
                        style={{
                          color: 'var(--text-primary)',
                          borderColor: 'var(--border-primary)'
                        }}
                      >
                        <div className="flex flex-col items-center justify-center gap-2 min-h-[3rem]">
                          {/* Column Title */}
                          <div className="flex items-center justify-center gap-1.5">
                            <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                              {col.label}
                            </span>
                            {col.tooltip && <ColumnInfoTooltip text={col.tooltip} />}
                          </div>

                          {/* Action Buttons Row */}
                          {(col.sortable || col.clickable) && (
                            <div className="flex items-center justify-center gap-1">
                              {/* Sort Button */}
                              {col.sortable && (
                                <button
                                  onClick={() => handleSort(col.sortKey || col.key)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                                  style={{
                                    backgroundColor: (col.sortKey || col.key) === sortColumn
                                      ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)'
                                      : 'var(--bg-tertiary)',
                                    color: (col.sortKey || col.key) === sortColumn
                                      ? 'var(--accent-cyan)'
                                      : 'var(--text-secondary)',
                                    border: '1px solid',
                                    borderColor: (col.sortKey || col.key) === sortColumn
                                      ? 'var(--accent-cyan)'
                                      : 'var(--border-primary)'
                                  }}
                                  title="مرتب‌سازی بر اساس این ستون"
                                >
                                  <svg
                                    className="w-3 h-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    {(col.sortKey || col.key) === sortColumn && sortDirection === 'desc' ? (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                                    ) : (
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                                    )}
                                  </svg>
                                  <span className="text-[10px] font-medium">
                                    {(col.sortKey || col.key) === sortColumn
                                      ? (sortDirection === 'asc' ? 'صعودی' : 'نزولی')
                                      : 'مرتب'}
                                  </span>
                                </button>
                              )}

                              {/* Chart Button */}
                              {col.clickable && !compact && (
                                <button
                                  onClick={() => handleColumnClick(col.key)}
                                  className="flex items-center gap-1 px-2 py-1 rounded-md transition-all hover:scale-105"
                                  style={{
                                    backgroundColor: selectedColumn === col.key
                                      ? 'rgba(var(--accent-cyan-rgb, 6, 182, 212), 0.2)'
                                      : 'var(--bg-tertiary)',
                                    color: selectedColumn === col.key
                                      ? 'var(--accent-cyan)'
                                      : 'var(--text-secondary)',
                                    border: '1px solid',
                                    borderColor: selectedColumn === col.key
                                      ? 'var(--accent-cyan)'
                                      : 'var(--border-primary)'
                                  }}
                                  title="نمایش نمودار مقایسه‌ای"
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                                  </svg>
                                  <span className="text-[10px] font-medium">نمودار</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRowsData.map((row: TableRowData) => (
                    <tr
                      key={row.uniqueKey}
                      className={`group transition-all border-b ${row.rowIndex % 2 === 0 ? '!bg-white dark:!bg-gray-900' : '!bg-gray-50 dark:!bg-gray-800'} hover:!bg-blue-50 dark:hover:!bg-gray-700`}
                      style={{
                        borderColor: 'var(--border-primary)'
                      }}
                    >
                      {displayColumns.map((col: TableColumn, colIndex: number) => (
                        <td
                          key={col.key}
                          className={`px-3 py-4 text-center text-sm font-medium border-l last:border-l-0 transition-colors ${colIndex === 0 ? 'text-text-primary font-bold bg-bg-tertiary' : 'text-text-secondary'}`}
                          style={{
                            borderColor: 'var(--border-primary)'
                          }}
                        >
                          {(() => {
                            // Type-safe property access
                            const value = row[col.key as keyof TableRowData];

                            // Handle formatting based on column key
                            switch (col.key) {
                              case 'invoiceCount':
                              case 'creditInvoiceCount':
                              case 'uniqueCustomers':
                                return (
                                  <div className="flex flex-col items-center">
                                    <span className="font-semibold">{formatNumberDisplay(value as number)}</span>
                                    {renderSparkline(col.key, row.rowIndex)}
                                  </div>
                                );
                              case 'totalAmount':
                              case 'avgInvoice':
                              case 'avgItemPrice':
                              case 'creditInvoiceAmount':
                              case 'usedCredit':
                                return (
                                  <div className="flex flex-col items-center">
                                    <span className="font-semibold">{formatCurrencyDisplay(value as number)}</span>
                                    {renderSparkline(col.key, row.rowIndex)}
                                  </div>
                                );
                              case 'subscription':
                              case 'creditPercent':
                              case 'creditUsedPercent':
                                if (col.key === 'subscription') {
                                  return (
                                    <div className="flex flex-col items-center">
                                      <div className="flex flex-col items-center gap-1">
                                        <span className="font-semibold">{formatPercent(value as number)}</span>
                                        {renderSubscriptionStatusIcon(value as number)}
                                      </div>
                                      {renderSparkline(col.key, row.rowIndex)}
                                    </div>
                                  );
                                }
                                if (col.key === 'creditPercent' || col.key === 'creditUsedPercent') {
                                  return (
                                    <div className="flex flex-col items-center">
                                      <span className="font-semibold">{formatPercent(value as number)}</span>
                                      {renderComparisonSparkline(col.key, row)}
                                    </div>
                                  );
                                }
                                return (
                                  <div className="flex flex-col items-center">
                                    <span className="font-semibold">{formatPercent(value as number)}</span>
                                    {renderSparkline(col.key, row.rowIndex)}
                                  </div>
                                );
                              case 'avgItemCount':
                                return (
                                  <span className="font-semibold">{(value as number)?.toFixed(2) || '-'}</span>
                                );
                              case 'priceGrowth':
                              case 'basketGrowth':
                                return (
                                  <div className="flex flex-col items-center justify-center">
                                    {col.render(value, row)}
                                    {renderSparkline(col.key, row.rowIndex)}
                                  </div>
                                );
                              case 'month':
                                return (
                                  <div className="flex flex-col items-center">
                                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{value as string}</span>
                                  </div>
                                );
                              case 'invoiceCountGrowth':
                              case 'totalAmountGrowth':
                                return (
                                  <div className="flex flex-col items-center">
                                    {col.render(value, row)}
                                    {renderSparkline(col.key, row.rowIndex)}
                                  </div>
                                );
                              default:
                                return <span className="font-medium">{String(value)}</span>;
                            }
                          })()}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {branchAverageRow && (
                    <tr className="border-y-2 bg-primary-100 dark:bg-primary-900/30 font-bold" style={{
                      borderColor: 'var(--border-secondary)'
                    }}>
                      {displayColumns.map((col: TableColumn, colIndex: number) => (
                        <td
                          key={`average-${col.key}`}
                          className="px-3 py-4 text-center text-sm font-bold border-l last:border-l-0"
                          style={{
                            borderColor: 'var(--border-primary)'
                          }}
                        >
                          {(() => {
                            if (colIndex === 0 || col.key === 'month') {
                              return (
                                <span style={{ color: 'var(--text-primary)' }}>میانگین</span>
                              );
                            }

                            if (AVERAGE_EXCLUDED_COLUMNS.has(col.key)) {
                              return <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
                            }

                            const rawValue = branchAverageRow[col.key as keyof BranchAverageData];
                            if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) {
                              return <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
                            }

                            switch (col.key) {
                              case 'invoiceCount':
                              case 'creditInvoiceCount':
                              case 'uniqueCustomers':
                                return <span>{formatNumberDisplay(rawValue)}</span>;
                              case 'totalAmount':
                              case 'avgInvoice':
                              case 'avgItemPrice':
                              case 'creditInvoiceAmount':
                              case 'usedCredit':
                                return <span>{formatCurrencyDisplay(rawValue)}</span>;
                              case 'subscription':
                              case 'creditPercent':
                              case 'creditUsedPercent':
                                return <span>{formatPercent(rawValue)}</span>;
                              case 'avgItemCount':
                                return <span>{rawValue.toFixed(2)}</span>;
                              default:
                                return <span>{rawValue}</span>;
                            }
                          })()}
                        </td>
                      ))}
                    </tr>
                  )}
                  {branchTotalsRow && (
                    <tr className="border-b-2 bg-primary-100 dark:bg-primary-900/30 font-bold" style={{
                      borderColor: 'var(--border-secondary)'
                    }}>
                      {displayColumns.map((col: TableColumn, colIndex: number) => (
                        <td
                          key={`total-${col.key}`}
                          className="px-3 py-4 text-center text-sm font-bold border-l last:border-l-0"
                          style={{
                            borderColor: 'var(--border-primary)'
                          }}
                        >
                          {(() => {
                            if (colIndex === 0 || col.key === 'month') {
                              return (
                                <span style={{ color: 'var(--text-primary)' }}>مجموع</span>
                              );
                            }

                            if (SUM_EXCLUDED_COLUMNS.has(col.key)) {
                              return <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
                            }

                            const rawValue = branchTotalsRow[col.key as keyof BranchTotalsData];
                            if (rawValue === null || typeof rawValue !== 'number' || !Number.isFinite(rawValue)) {
                              return <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
                            }

                            switch (col.key) {
                              case 'invoiceCount':
                              case 'creditInvoiceCount':
                              case 'uniqueCustomers':
                                return <span>{formatNumberDisplay(rawValue)}</span>;
                              case 'totalAmount':
                              case 'creditInvoiceAmount':
                              case 'usedCredit':
                                return <span>{formatCurrencyDisplay(rawValue)}</span>;
                              case 'creditPercent':
                              case 'creditUsedPercent':
                                return <span>{formatPercent(rawValue)}</span>;
                              default:
                                return <span>{rawValue}</span>;
                            }
                          })()}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
        </HorizontalTableShell>
      </div>

      {/* Enhanced Comparison Chart */}
      {showChart && selectedColumn && !compact && (
        <>
          <h3 className="title-section">
            تحلیل مقایسه‌ای
          </h3>
          <div ref={chartSectionRef} className="card-base card-padding animate-enter animate-delay-3">
            <div className="flex items-center justify-between mb-4">
              <ChartInfoButton
                onClick={() => openGuide(CHART_GUIDES.branchComparison)}
              />
              <button
                onClick={() => setShowChart(false)}
                className="btn btn-ghost inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-secondary hover:bg-bg-tertiary transition-all"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden lg:inline">بستن نمودار</span>
              </button>
            </div>

            <h3 className="title-card">
              {getColumnLabel(selectedColumn)}
            </h3>
            <p className="text-sm mt-1 text-text-secondary text-center">
              شعبه {data.branchInfo.BranchName} در مقایسه با کل گروه و کل بازار
            </p>

            {/* Enhanced Warning for Logarithmic Scale */}
            {/* {['totalAmount', 'usedCredit'].includes(selectedColumn) && (
              <div className="alert alert-warning mb-6">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-medium">
                    نمودار با مقیاس لگاریتمی نمایش داده می‌شود
                  </p>
                  <p className="text-sm mt-1">
                    این مقیاس برای نمایش بهتر اختلاف قابل توجه در مقادیر استفاده شده است
                  </p>
                </div>
              </div>
            )} */}

            {/* Enhanced Chart Container */}
            {shouldUseBarChart ? (
              <BarChart
                key={`chart-${selectedColumn}-${branchId}-${Date.now()}`}
                data={comparisonChartData}
                height={400}
                plugins={[barValueLabelPlugin, zeroBaselinePlugin]}
                options={{
                  ...getChartJsOptions(currentTheme),
                  scales: {
                    ...getChartJsOptions(currentTheme).scales,
                    x: {
                      ...getChartJsOptions(currentTheme).scales?.x,
                      ticks: {
                        ...getChartJsOptions(currentTheme).scales?.x?.ticks,
                        autoSkip: false,
                        maxRotation: 45,
                        minRotation: 45,
                      },
                    },
                    y: {
                      ...getChartJsOptions(currentTheme).scales?.y,
                      type: ['totalAmount', 'usedCredit'].includes(selectedColumn) ? 'logarithmic' : 'linear',
                      min: barChartYAxisBounds?.min,
                      max: barChartYAxisBounds?.max,
                      beginAtZero: barChartYAxisBounds?.min === 0,
                      afterBuildTicks: (axis: any) => {
                        if (!axis?.ticks) return;
                        const hasZeroTick = axis.ticks.some((tick: any) => tick?.value === 0);
                        if (!hasZeroTick) {
                          axis.ticks.push({ value: 0 });
                          axis.ticks.sort((first: any, second: any) => first.value - second.value);
                        }
                      },
                      ticks: {
                        ...(getChartJsOptions(currentTheme).scales?.y as any)?.ticks,
                        includeBounds: false,
                        // بدون ارقام اعشاری روی محور Y — همیشه عدد صحیح فارسی
                        precision: 0,
                        callback: (value: any) =>
                          toPersianDigits(Math.round(Number(value)).toLocaleString('en-US')),
                      },
                      // فقط zeroBaselinePlugin خط صفر را رسم می‌کند (یک خط، نه دو خط)
                      grid: {
                        display: false,
                      }
                    }
                  },
                  plugins: {
                    ...getChartJsOptions(currentTheme).plugins,
                    tooltip: {
                      ...getChartJsOptions(currentTheme).plugins?.tooltip,
                      callbacks: {
                        ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                        label: function (context: TooltipItem<'line' | 'bar'>) {
                          const value = context.parsed.y;
                          const formattedValue = formatChartValue(value, selectedColumn);
                          const label = context.dataset.label || '';
                          return formatTooltipLabelValue(label, formattedValue);
                        }
                      }
                    }
                  }
                }}
              />
            ) : (
              <LineChart
                key={`chart-${selectedColumn}-${branchId}-${Date.now()}`}
                data={comparisonChartData}
                height={400}
                plugins={[zeroBaselinePlugin]}
                options={{
                  ...getChartJsOptions(currentTheme),
                  scales: {
                    ...getChartJsOptions(currentTheme).scales,
                    y: {
                      ...getChartJsOptions(currentTheme).scales?.y,
                      type: ['totalAmount', 'usedCredit'].includes(selectedColumn) ? 'logarithmic' : 'linear',
                      beginAtZero: true,
                      ticks: {
                        ...(getChartJsOptions(currentTheme).scales?.y as any)?.ticks,
                        // بدون ارقام اعشاری روی محور Y — همیشه عدد صحیح فارسی
                        precision: 0,
                        callback: (value: any) =>
                          toPersianDigits(Math.round(Number(value)).toLocaleString('en-US')),
                      },
                    }
                  },
                  plugins: {
                    ...getChartJsOptions(currentTheme).plugins,
                    tooltip: {
                      ...getChartJsOptions(currentTheme).plugins?.tooltip,
                      callbacks: {
                        ...(getChartJsOptions(currentTheme).plugins?.tooltip?.callbacks || {}),
                        label: function (context: TooltipItem<'line' | 'bar'>) {
                          const value = context.parsed.y;
                          const formattedValue = formatChartValue(value, selectedColumn);
                          const label = context.dataset.label || '';
                          return formatTooltipLabelValue(label, formattedValue);
                        }
                      }
                    }
                  }
                }}
              />
            )}
          </div>

          {/* Enhanced Statistics Summary */}
          {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <KPICard
              title={`شعبه ${data.branchInfo.BranchName}`}
              value={formatChartValue(
                data.branchData.reduce((sum: number, row: BranchData) => {
                  const val = selectedColumn in row ? (row[selectedColumn as keyof BranchData] as number) || 0 : 0;
                  return sum + val;
                }, 0) / data.branchData.length,
                selectedColumn
              )}
              subtitle="میانگین دوره"
              variant="info"
              accentStyle="corner"
            />

            <KPICard
              title="کل گروه"
              value={data.brandData.length > 0 ? formatChartValue(
                data.brandData.reduce((sum: number, row: BranchData) => {
                  const val = selectedColumn in row ? (row[selectedColumn as keyof BranchData] as number) || 0 : 0;
                  return sum + val;
                }, 0) / data.brandData.length,
                selectedColumn
              ) : '-'}
              subtitle="میانگین دوره"
              variant="success"
              accentStyle="corner"
            />
          </div> */}
        </>
      )}

      {/* Chart Guide Modal */}
      {currentGuide && (
        <ChartGuideModal
          isOpen={isModalOpen}
          onClose={closeGuide}
          title={currentGuide.title}
        >
          <ChartGuideBody guide={currentGuide} />
        </ChartGuideModal>
      )}
    </>
  );
};

export default MonthlyPerformanceTable;
